import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Chip,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import TableActionButton from "../../components/layout/TableActionButton";
import customerAPI, { Customer, CustomerPayload } from "../../services/customerAPI";
import reportAPI from "../../services/reportAPI";
import { rowsToCsv } from "../../utils/csvExport";
import { useRBAC } from "../../hooks/useRBAC";

const emptyForm = (): CustomerPayload => ({
  name: "",
  phone: "",
  email: "",
  gstin: "",
  pan: "",
  city: "",
  state: "",
  stateCode: "",
  address: "",
  isActive: true,
});

const CustomersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete, canRead } = useRBAC();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form, setForm] = useState<CustomerPayload>(emptyForm());
  const [exporting, setExporting] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["customers", page, rowsPerPage, search],
    queryFn: () =>
      customerAPI.getCustomers({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        search: search || undefined,
      }),
  });

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: async () => {
      if (editing) return customerAPI.updateCustomer(editing.id, form);
      return customerAPI.createCustomer(form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customersAll"] });
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      setAlert({ type: "success", message: editing ? "Customer updated." : "Customer created." });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to save customer",
      });
    },
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: string) => customerAPI.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setAlert({ type: "success", message: "Customer deleted." });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete customer",
      });
    },
  });

  async function handleTallyExport() {
    setExporting(true);
    try {
      const data = await reportAPI.getTallyPartiesExport();
      const csv = rowsToCsv(data.rows || []);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tally_parties.csv";
      link.click();
      URL.revokeObjectURL(url);
      setAlert({ type: "success", message: "Tally party master CSV downloaded." });
    } catch (err: any) {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to export parties",
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <Box>
      <PageHeader
        title="Customers"
        subtitle="B2B / walk-in customers (non-NFC) for invoices and party ledger"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Customers" },
        ]}
        action={
          canCreate("sales") || canRead("reports") ? (
            <Stack direction="row" spacing={1}>
              {canRead("reports") && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={exporting}
                  onClick={() => void handleTallyExport()}
                >
                  Tally export
                </Button>
              )}
              {canCreate("sales") ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditing(null);
                    setForm(emptyForm());
                    setOpen(true);
                  }}
                >
                  Add customer
                </Button>
              ) : null}
            </Stack>
          ) : undefined
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || "Failed to load customers"}
        </Alert>
      )}

      <DataListCard
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search name, phone, GSTIN…"
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>GSTIN</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              ) : (data?.data || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No customers yet.</TableCell>
                </TableRow>
              ) : (
                (data?.data || []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.phone || "—"}</TableCell>
                    <TableCell>{row.gstin || "—"}</TableCell>
                    <TableCell>{row.city || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.isActive ? "Active" : "Inactive"}
                        color={row.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canUpdate("sales") && (
                          <TableActionButton
                            title="Edit"
                            variant="primary"
                            onClick={() => {
                              setEditing(row);
                              setForm({
                                name: row.name,
                                phone: row.phone || "",
                                email: row.email || "",
                                gstin: row.gstin || "",
                                pan: row.pan || "",
                                city: row.city || "",
                                state: row.state || "",
                                stateCode: row.stateCode || "",
                                address: row.address || "",
                                isActive: row.isActive,
                              });
                              setOpen(true);
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canDelete("sales") && (
                          <TableActionButton
                            title="Delete"
                            variant="danger"
                            onClick={() => {
                              if (window.confirm(`Delete customer "${row.name}"?`)) {
                                remove(row.id);
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.total || 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DataListCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit customer" : "Add customer"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone || ""}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={form.email || ""}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GSTIN"
                value={form.gstin || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, gstin: e.target.value.toUpperCase() }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN"
                value={form.pan || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, pan: e.target.value.toUpperCase() }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State code"
                value={form.stateCode || ""}
                onChange={(e) => setForm((p) => ({ ...p, stateCode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={form.city || ""}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={form.state || ""}
                onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                minRows={2}
                value={form.address || ""}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive !== false}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, isActive: e.target.checked }))
                    }
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || !form.name.trim()}
            onClick={() => save()}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomersPage;
